/**
 * Which of a person's own details they may change themselves, and which need HR to approve first.
 *
 * Pure and shared on purpose, like `decideQuickStatusChange`: the API enforces it and the web form
 * labels every field from it, so what the form promises ("applied instantly" / "needs HR approval")
 * can never disagree with what the server actually does.
 *
 * THE RULE OF THUMB. A change needs approval when a wrong or fraudulent value would cost money or
 * break a legal record: where salary is paid, tax and identity numbers, date of birth, legal name,
 * and anything that drives benefits eligibility. Contact details and preferences apply at once —
 * making someone wait a day for HR to approve a new phone number helps nobody. Job facts
 * (department, manager, grade, shift, pay) are not in this list at all: they belong to HR and are
 * not self-editable in any form.
 */

export const PROFILE_FIELD_KEYS = [
  'phone',
  'personalEmail',
  'gender',
  'addressLine1',
  'city',
  'stateCode',
  'postalCode',
  'country',
  'emergencyContactName',
  'emergencyContactPhone',
  'emergencyContactRelation',
  'firstName',
  'lastName',
  'maritalStatus',
  'dateOfBirth',
  'aadhaar',
  'pan',
  'bankName',
  'branch',
  'bankCode1',
  'accountNumber',
] as const;
export type ProfileFieldKey = (typeof PROFILE_FIELD_KEYS)[number];

export type ProfileFieldRule = 'instant' | 'approval';

/** The request categories HR's review queue groups by. */
export const PROFILE_FIELD_CATEGORIES = [
  'Personal',
  'Address',
  'Bank Account',
  'Tax & Statutory',
  'Emergency Contact',
] as const;
export type ProfileFieldCategory = (typeof PROFILE_FIELD_CATEGORIES)[number];

/**
 * Values that must never sit readable in a pending request's JSON (CLAUDE.md invariant 7). The
 * request stores a mask for HR to see and the real value sealed under the person's own key.
 */
export type SensitiveKind = 'dob' | 'aadhaar' | 'pan' | 'account_number';

export interface ProfileFieldSpec {
  readonly label: string;
  readonly rule: ProfileFieldRule;
  readonly category: ProfileFieldCategory;
  readonly sensitive?: SensitiveKind;
}

export const PROFILE_FIELDS: Readonly<Record<ProfileFieldKey, ProfileFieldSpec>> = {
  // Instant — contact details and preferences.
  phone: { label: 'Phone Number', rule: 'instant', category: 'Personal' },
  personalEmail: { label: 'Personal Email', rule: 'instant', category: 'Personal' },
  gender: { label: 'Gender', rule: 'instant', category: 'Personal' },
  addressLine1: { label: 'Address', rule: 'instant', category: 'Address' },
  city: { label: 'City', rule: 'instant', category: 'Address' },
  stateCode: { label: 'State', rule: 'instant', category: 'Address' },
  postalCode: { label: 'Postal Code', rule: 'instant', category: 'Address' },
  country: { label: 'Country', rule: 'instant', category: 'Address' },
  emergencyContactName: {
    label: 'Emergency Contact Name',
    rule: 'instant',
    category: 'Emergency Contact',
  },
  emergencyContactPhone: {
    label: 'Emergency Contact Phone',
    rule: 'instant',
    category: 'Emergency Contact',
  },
  emergencyContactRelation: {
    label: 'Emergency Contact Relationship',
    rule: 'instant',
    category: 'Emergency Contact',
  },

  // Approval — identity, tax, benefits and money.
  firstName: { label: 'First Name', rule: 'approval', category: 'Personal' },
  lastName: { label: 'Last Name', rule: 'approval', category: 'Personal' },
  maritalStatus: { label: 'Marital Status', rule: 'approval', category: 'Personal' },
  dateOfBirth: {
    label: 'Date of Birth',
    rule: 'approval',
    category: 'Personal',
    sensitive: 'dob',
  },
  aadhaar: {
    label: 'Aadhaar Number',
    rule: 'approval',
    category: 'Tax & Statutory',
    sensitive: 'aadhaar',
  },
  pan: { label: 'PAN', rule: 'approval', category: 'Tax & Statutory', sensitive: 'pan' },
  bankName: { label: 'Bank Name', rule: 'approval', category: 'Bank Account' },
  branch: { label: 'Bank Branch', rule: 'approval', category: 'Bank Account' },
  bankCode1: { label: 'IFSC / Bank Code', rule: 'approval', category: 'Bank Account' },
  accountNumber: {
    label: 'Account Number',
    rule: 'approval',
    category: 'Bank Account',
    sensitive: 'account_number',
  },
};

export function isProfileFieldKey(value: string): value is ProfileFieldKey {
  return (PROFILE_FIELD_KEYS as readonly string[]).includes(value);
}

export function profileFieldRule(key: ProfileFieldKey): ProfileFieldRule {
  return PROFILE_FIELDS[key].rule;
}

/** Split the keys a person is changing into what applies now and what waits for HR. */
export function splitProfileFieldKeys(keys: readonly ProfileFieldKey[]): {
  instant: ProfileFieldKey[];
  approval: ProfileFieldKey[];
} {
  return {
    instant: keys.filter((key) => PROFILE_FIELDS[key].rule === 'instant'),
    approval: keys.filter((key) => PROFILE_FIELDS[key].rule === 'approval'),
  };
}

// ---------------------------------------------------------------------------
// Family members and education
// ---------------------------------------------------------------------------

/**
 * A family member is instant unless it touches a DEPENDENT — insurance and tax benefits hang off
 * that flag, so adding one, removing one, or flipping the flag either way is HR's call.
 * `wasDependent` is the stored value (false when adding), `willBeDependent` the requested one
 * (false when removing).
 */
export function familyChangeNeedsApproval(
  wasDependent: boolean,
  willBeDependent: boolean,
): boolean {
  return wasDependent || willBeDependent;
}

/**
 * Education is a first-time entry while someone is still being onboarded (nothing on file for HR to
 * compare against, so approval would be approving an unverified claim against nothing), and a
 * change to the record afterwards — background checks rely on it by then.
 */
export function educationChangeNeedsApproval(hasJoined: boolean): boolean {
  return hasJoined;
}

/** Statuses from the day someone has actually started. `pre_joining` is still onboarding. */
const JOINED_EMPLOYMENT_STATUSES: ReadonlySet<string> = new Set([
  'probation',
  'confirmed',
  'on_leave',
  'notice_period',
]);

export function hasJoinedByStatus(statuses: readonly string[]): boolean {
  return statuses.some((status) => JOINED_EMPLOYMENT_STATUSES.has(status));
}
